import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  BanknotesIcon,
  CheckCircleIcon,
  ClockIcon,
  DocumentPlusIcon,
  LinkIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { apiService, KitDigitalClient, KitDigitalStats } from '../services/apiService';
import { Company } from '../types';
import { canAccessKitDigital } from '../utils/kitDigitalAccess';

interface KitDigitalPanelProps {
  currentCompany: Company | null;
  userEmail?: string;
  userRole?: string;
}

const emptyRow = (): Partial<KitDigitalClient> => ({
  clientName: '',
  url: '',
  pago70: 1400,
  pago30: 600,
  feeTalentosoft: 180,
  ordenPago: null,
});

const eur = (n: number) =>
  `${n.toLocaleString('es-ES', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} €`;

const net70 = (row: KitDigitalClient) =>
  row.netInvoice70 ?? Math.max(0, (row.pago70 || 0) - (row.feeTalentosoft || 180));

export const KitDigitalPanel: React.FC<KitDigitalPanelProps> = ({
  currentCompany,
  userEmail,
  userRole,
}) => {
  const [clients, setClients] = useState<KitDigitalClient[]>([]);
  const [stats, setStats] = useState<KitDigitalStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [draft, setDraft] = useState(emptyRow());
  const [filterSplitDris, setFilterSplitDris] = useState(false);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 3500);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await apiService.getKitDigitalClients();
    if (res.error) {
      setError(res.message || res.error);
      setClients([]);
      setStats(null);
    } else {
      const data = res.data as { clients?: KitDigitalClient[]; stats?: KitDigitalStats };
      setClients(data?.clients || []);
      setStats(data?.stats || null);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (canAccessKitDigital(currentCompany, userEmail, userRole)) load();
    else setLoading(false);
    return () => {
      if (toastTimer.current) clearTimeout(toastTimer.current);
    };
  }, [currentCompany, userEmail, userRole, load]);

  const selected = useMemo(
    () => clients.find((c) => c.id === selectedId) || null,
    [clients, selectedId]
  );

  const visibleClients = useMemo(
    () => (filterSplitDris ? clients.filter((c) => c.splitWithDris) : clients),
    [clients, filterSplitDris]
  );

  const patchClient = async (id: string, patch: Partial<KitDigitalClient>, silent = true) => {
    setBusyId(id);
    setError(null);
    const res = await apiService.patchKitDigitalClient(id, patch);
    setBusyId(null);
    if (res.error) {
      setError(res.message || res.error);
      return false;
    }
    if (!silent) showToast('Cambios guardados');
    await load();
    return true;
  };

  const handleAdd = async () => {
    setError(null);
    const res = await apiService.createKitDigitalClient({
      clientName: draft.clientName,
      url: draft.url,
      pago70: Number(draft.pago70),
      pago30: Number(draft.pago30),
      feeTalentosoft: Number(draft.feeTalentosoft),
      ordenPago: draft.ordenPago,
    });
    if (res.error) {
      setError(res.message || res.error);
      return;
    }
    setShowAdd(false);
    setDraft(emptyRow());
    showToast('Cliente añadido');
    await load();
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('¿Eliminar este cliente Kit Digital?')) return;
    setBusyId(id);
    const res = await apiService.deleteKitDigitalClient(id);
    setBusyId(null);
    if (res.error) {
      setError(res.message || res.error);
      return;
    }
    if (selectedId === id) setSelectedId(null);
    await load();
  };

  const runAction = async (
    id: string,
    fn: () => Promise<{ error?: string; message?: string; data?: unknown }>,
    okMessage?: string
  ) => {
    setBusyId(id);
    setError(null);
    const res = await fn();
    setBusyId(null);
    if (res.error) {
      setError(res.message || res.error);
      return;
    }
    const d = res.data as { invoiceNumber?: string; duplicate?: boolean };
    if (okMessage) showToast(okMessage);
    else if (d?.invoiceNumber) {
      showToast(d.duplicate ? `Ya facturada: ${d.invoiceNumber}` : `Factura ${d.invoiceNumber}`);
    }
    await load();
  };

  if (!canAccessKitDigital(currentCompany, userEmail, userRole)) {
    return (
      <div className="rounded-2xl border border-zinc-200 bg-white p-8 text-zinc-600 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-400">
        Kit Digital no disponible para tu cuenta.
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-2xl">
          <h2 className="text-2xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">
            Kit Digital
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-zinc-500 dark:text-zinc-400">
            1.er cobro: importe 70% menos comisión Talento Solution (180 € por defecto). 2.º cobro:
            30% restante. Los checkboxes KD indican si ya emitiste la factura Kit Digital; el cobro
            se marca con los botones 70% / 30%.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowAdd(true)}
          className="inline-flex shrink-0 items-center gap-2 rounded-xl bg-zinc-900 px-5 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-zinc-800 dark:bg-zinc-100 dark:text-zinc-900 dark:hover:bg-white"
        >
          <PlusIcon className="h-4 w-4" />
          Añadir cliente
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setFilterSplitDris((v) => !v)}
          className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
            filterSplitDris
              ? 'border-violet-400 bg-violet-100 text-violet-900 dark:border-violet-600 dark:bg-violet-950/50 dark:text-violet-200'
              : 'border-zinc-300 bg-white text-zinc-600 hover:border-violet-300 dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-400'
          }`}
        >
          Solo a medias con Dris
          {(stats?.countSplitWithDris ?? clients.filter((c) => c.splitWithDris).length) > 0 && (
            <span className="ml-1.5 tabular-nums opacity-80">
              ({stats?.countSplitWithDris ?? clients.filter((c) => c.splitWithDris).length})
            </span>
          )}
        </button>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200">
          {error}
        </div>
      )}
      {toast && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-200">
          {toast}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {[
          {
            label: 'Total kits',
            value: String(stats?.totalKits ?? clients.length),
            icon: BanknotesIcon,
            hint: 'Clientes en tabla',
          },
          {
            label: 'Cobrado 70%',
            value: eur(stats?.cobrado70 ?? 0),
            icon: CheckCircleIcon,
            hint: 'Suma con fecha cobro 70%',
          },
          {
            label: 'Pendiente 70%',
            value: eur(stats?.pendiente70 ?? 0),
            icon: ClockIcon,
            hint: `${stats?.countPending70 ?? '—'} sin cobrar (neto)`,
          },
          {
            label: 'Pendiente 30%',
            value: eur(stats?.pendiente30 ?? 0),
            icon: DocumentPlusIcon,
            hint: `${stats?.countPending30 ?? '—'} sin cobrar`,
          },
        ].map((card) => (
          <div
            key={card.label}
            className="rounded-2xl border border-zinc-200/90 bg-white p-5 shadow-sm dark:border-zinc-700 dark:bg-zinc-900"
          >
            <card.icon className="mb-3 h-5 w-5 text-sky-600" />
            <p className="text-xs font-medium uppercase tracking-wider text-zinc-500">
              {card.label}
            </p>
            <p className="mt-1 text-2xl font-semibold tabular-nums text-zinc-900 dark:text-zinc-50">
              {card.value}
            </p>
            <p className="mt-1 text-xs text-zinc-400">{card.hint}</p>
          </div>
        ))}
      </div>

      {showAdd && (
        <div className="rounded-2xl border border-zinc-200 bg-zinc-50/80 p-5 dark:border-zinc-700 dark:bg-zinc-800/40">
          <p className="mb-4 text-sm font-semibold text-zinc-800 dark:text-zinc-200">Nuevo cliente</p>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <input
              className="rounded-lg border border-zinc-300 bg-white px-3 py-2.5 text-sm dark:border-zinc-600 dark:bg-zinc-900"
              placeholder="Cliente"
              value={draft.clientName || ''}
              onChange={(e) => setDraft((d) => ({ ...d, clientName: e.target.value }))}
            />
            <input
              className="rounded-lg border border-zinc-300 bg-white px-3 py-2.5 text-sm dark:border-zinc-600 dark:bg-zinc-900"
              placeholder="URL"
              value={draft.url || ''}
              onChange={(e) => setDraft((d) => ({ ...d, url: e.target.value }))}
            />
            <input
              type="number"
              className="rounded-lg border border-zinc-300 bg-white px-3 py-2.5 text-sm dark:border-zinc-600 dark:bg-zinc-900"
              placeholder="Pago 70%"
              value={draft.pago70 ?? ''}
              onChange={(e) => setDraft((d) => ({ ...d, pago70: Number(e.target.value) }))}
            />
            <input
              type="number"
              className="rounded-lg border border-zinc-300 bg-white px-3 py-2.5 text-sm dark:border-zinc-600 dark:bg-zinc-900"
              placeholder="Pago 30%"
              value={draft.pago30 ?? ''}
              onChange={(e) => setDraft((d) => ({ ...d, pago30: Number(e.target.value) }))}
            />
          </div>
          <div className="mt-4 flex gap-2">
            <button
              type="button"
              onClick={handleAdd}
              className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700"
            >
              Guardar
            </button>
            <button
              type="button"
              onClick={() => setShowAdd(false)}
              className="rounded-lg border border-zinc-300 px-4 py-2 text-sm dark:border-zinc-600"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-zinc-200/90 bg-white shadow-sm dark:border-zinc-700 dark:bg-zinc-900">
        <div className="overflow-x-auto">
          <table className="min-w-[1280px] w-full border-separate border-spacing-0 text-sm">
            <thead>
              <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
                <th className="sticky left-0 z-10 min-w-[52px] border-b border-zinc-200 bg-zinc-50 px-3 py-3 dark:border-zinc-700 dark:bg-zinc-800/90">
                  Orden
                </th>
                <th className="min-w-[160px] border-b border-zinc-200 bg-zinc-50 px-3 py-3 dark:border-zinc-700 dark:bg-zinc-800/90">
                  Cliente
                </th>
                <th className="min-w-[140px] border-b border-zinc-200 bg-zinc-50 px-3 py-3 dark:border-zinc-700 dark:bg-zinc-800/90">
                  URL
                </th>
                <th className="min-w-[88px] border-b border-zinc-200 bg-zinc-50 px-3 py-3 text-right dark:border-zinc-700 dark:bg-zinc-800/90">
                  Pago 70%
                </th>
                <th className="min-w-[100px] border-b border-zinc-200 bg-zinc-50 px-3 py-3 text-right dark:border-zinc-700 dark:bg-zinc-800/90">
                  Neto fact. 70%
                </th>
                <th className="min-w-[88px] border-b border-zinc-200 bg-zinc-50 px-3 py-3 text-right dark:border-zinc-700 dark:bg-zinc-800/90">
                  Pago 30%
                </th>
                <th className="min-w-[72px] border-b border-zinc-200 bg-zinc-50 px-3 py-3 text-right dark:border-zinc-700 dark:bg-zinc-800/90">
                  Fee
                </th>
                <th className="min-w-[88px] border-b border-zinc-200 bg-zinc-50 px-3 py-3 text-center dark:border-zinc-700 dark:bg-zinc-800/90">
                  Cobro 70%
                </th>
                <th className="min-w-[88px] border-b border-zinc-200 bg-zinc-50 px-3 py-3 text-center dark:border-zinc-700 dark:bg-zinc-800/90">
                  Cobro 30%
                </th>
                <th className="min-w-[100px] border-b border-zinc-200 bg-zinc-50 px-3 py-3 text-center dark:border-zinc-700 dark:bg-zinc-800/90">
                  Fact. KD 70%
                </th>
                <th className="min-w-[100px] border-b border-zinc-200 bg-zinc-50 px-3 py-3 text-center dark:border-zinc-700 dark:bg-zinc-800/90">
                  Fact. KD 30%
                </th>
                <th className="min-w-[108px] border-b border-zinc-200 bg-zinc-50 px-3 py-3 text-center dark:border-zinc-700 dark:bg-zinc-800/90">
                  50% Dris
                </th>
                <th className="min-w-[220px] border-b border-zinc-200 bg-zinc-50 px-3 py-3 dark:border-zinc-700 dark:bg-zinc-800/90">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={13} className="px-4 py-12 text-center text-zinc-500">
                    Cargando…
                  </td>
                </tr>
              ) : visibleClients.length === 0 ? (
                <tr>
                  <td colSpan={13} className="px-4 py-12 text-center text-zinc-500">
                    {filterSplitDris
                      ? 'Ningún kit marcado a medias con Dris.'
                      : 'Sin clientes. Añade el primero desde el botón superior.'}
                  </td>
                </tr>
              ) : (
                visibleClients.map((row) => {
                  const paid70 = !!row.paid70At;
                  const paid30 = !!row.paid30At;
                  const complete = paid70 && paid30;
                  const isSelected = selectedId === row.id;
                  const rowBusy = busyId === row.id;
                  const n70 = net70(row);

                  return (
                    <tr
                      key={row.id}
                      onClick={() => setSelectedId(row.id)}
                      className={`cursor-pointer transition-colors ${
                        complete
                          ? 'bg-emerald-50/90 dark:bg-emerald-950/25'
                          : isSelected
                            ? 'bg-amber-50/80 dark:bg-amber-950/20'
                            : 'hover:bg-zinc-50/80 dark:hover:bg-zinc-800/30'
                      }`}
                    >
                      <td
                        className={`sticky left-0 border-b border-zinc-100 px-3 py-2.5 dark:border-zinc-800 ${
                          complete
                            ? 'bg-emerald-50/90 dark:bg-emerald-950/25'
                            : isSelected
                              ? 'bg-amber-50/80 dark:bg-amber-950/20'
                              : 'bg-white dark:bg-zinc-900'
                        }`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="number"
                          aria-label="Orden de pago"
                          className="w-12 rounded-md border border-zinc-200 bg-white px-2 py-1 text-center text-xs tabular-nums dark:border-zinc-600 dark:bg-zinc-800"
                          value={row.ordenPago ?? ''}
                          onChange={(e) => {
                            const v = e.target.value ? Number(e.target.value) : null;
                            setClients((list) =>
                              list.map((c) => (c.id === row.id ? { ...c, ordenPago: v } : c))
                            );
                          }}
                          onBlur={(e) => {
                            const v = e.target.value ? Number(e.target.value) : null;
                            patchClient(row.id, { ordenPago: v });
                          }}
                        />
                      </td>
                      <td className="border-b border-zinc-100 px-3 py-2.5 dark:border-zinc-800">
                        <input
                          className="w-full min-w-[140px] rounded-md border border-transparent bg-transparent px-2 py-1 font-medium hover:border-zinc-300 focus:border-sky-400 focus:outline-none dark:hover:border-zinc-600"
                          value={row.clientName}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) =>
                            setClients((list) =>
                              list.map((c) =>
                                c.id === row.id ? { ...c, clientName: e.target.value } : c
                              )
                            )
                          }
                          onBlur={(e) => patchClient(row.id, { clientName: e.target.value })}
                        />
                        {row.splitWithDris && (
                          <span className="mt-1 inline-flex rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-violet-800 dark:bg-violet-900/50 dark:text-violet-200">
                            50% Dris
                          </span>
                        )}
                      </td>
                      <td className="border-b border-zinc-100 px-3 py-2.5 dark:border-zinc-800">
                        <input
                          className="w-full min-w-[120px] rounded-md border border-transparent bg-transparent px-2 py-1 text-xs text-sky-700 focus:border-sky-400 focus:outline-none dark:text-sky-400"
                          value={row.url || ''}
                          placeholder="https://"
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) =>
                            setClients((list) =>
                              list.map((c) => (c.id === row.id ? { ...c, url: e.target.value } : c))
                            )
                          }
                          onBlur={(e) => patchClient(row.id, { url: e.target.value })}
                        />
                      </td>
                      <td
                        className="border-b border-zinc-100 px-3 py-2.5 text-right dark:border-zinc-800"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="number"
                          className="w-20 rounded-md border border-zinc-200 bg-white px-2 py-1 text-right text-xs tabular-nums dark:border-zinc-600 dark:bg-zinc-800"
                          value={row.pago70}
                          onChange={(e) => {
                            const v = Number(e.target.value);
                            setClients((list) =>
                              list.map((c) => (c.id === row.id ? { ...c, pago70: v } : c))
                            );
                          }}
                          onBlur={(e) => patchClient(row.id, { pago70: Number(e.target.value) })}
                        />
                      </td>
                      <td className="border-b border-zinc-100 px-3 py-2.5 text-right tabular-nums text-zinc-600 dark:border-zinc-800 dark:text-zinc-400">
                        {eur(n70)}
                      </td>
                      <td
                        className="border-b border-zinc-100 px-3 py-2.5 text-right dark:border-zinc-800"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="number"
                          className="w-20 rounded-md border border-zinc-200 bg-white px-2 py-1 text-right text-xs tabular-nums dark:border-zinc-600 dark:bg-zinc-800"
                          value={row.pago30}
                          onChange={(e) => {
                            const v = Number(e.target.value);
                            setClients((list) =>
                              list.map((c) => (c.id === row.id ? { ...c, pago30: v } : c))
                            );
                          }}
                          onBlur={(e) => patchClient(row.id, { pago30: Number(e.target.value) })}
                        />
                      </td>
                      <td
                        className="border-b border-zinc-100 px-3 py-2.5 text-right dark:border-zinc-800"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="number"
                          className="w-16 rounded-md border border-zinc-200 bg-white px-2 py-1 text-right text-xs tabular-nums dark:border-zinc-600 dark:bg-zinc-800"
                          value={row.feeTalentosoft}
                          onChange={(e) => {
                            const v = Number(e.target.value);
                            setClients((list) =>
                              list.map((c) =>
                                c.id === row.id ? { ...c, feeTalentosoft: v } : c
                              )
                            );
                          }}
                          onBlur={(e) =>
                            patchClient(row.id, { feeTalentosoft: Number(e.target.value) })
                          }
                        />
                      </td>
                      <td className="border-b border-zinc-100 px-3 py-2.5 text-center dark:border-zinc-800">
                        {paid70 ? (
                          <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200">
                            ✓
                          </span>
                        ) : (
                          <span className="text-xs text-zinc-400">—</span>
                        )}
                      </td>
                      <td className="border-b border-zinc-100 px-3 py-2.5 text-center dark:border-zinc-800">
                        {paid30 ? (
                          <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200">
                            ✓
                          </span>
                        ) : (
                          <span className="text-xs text-zinc-400">—</span>
                        )}
                      </td>
                      <td
                        className="border-b border-zinc-100 px-3 py-2.5 text-center dark:border-zinc-800"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <label className="inline-flex cursor-pointer items-center gap-1.5 text-xs text-zinc-600 dark:text-zinc-400">
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-zinc-300 text-sky-600"
                            checked={!!row.facturaKd70}
                            title="Factura Kit Digital del 70% emitida"
                            onChange={(e) => {
                              const v = e.target.checked;
                              setClients((list) =>
                                list.map((c) => (c.id === row.id ? { ...c, facturaKd70: v } : c))
                              );
                              patchClient(row.id, { facturaKd70: v });
                            }}
                          />
                          <span className="sr-only sm:not-sr-only">Emitida</span>
                        </label>
                      </td>
                      <td
                        className="border-b border-zinc-100 px-3 py-2.5 text-center dark:border-zinc-800"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <label className="inline-flex cursor-pointer items-center gap-1.5 text-xs text-zinc-600 dark:text-zinc-400">
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-zinc-300 text-sky-600"
                            checked={!!row.facturaKd30}
                            title="Factura Kit Digital del 30% emitida"
                            onChange={(e) => {
                              const v = e.target.checked;
                              setClients((list) =>
                                list.map((c) => (c.id === row.id ? { ...c, facturaKd30: v } : c))
                              );
                              patchClient(row.id, { facturaKd30: v });
                            }}
                          />
                          <span className="sr-only sm:not-sr-only">Emitida</span>
                        </label>
                      </td>
                      <td
                        className="border-b border-zinc-100 px-3 py-2.5 text-center dark:border-zinc-800"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <label className="inline-flex cursor-pointer items-center justify-center gap-1.5 text-xs text-zinc-600 dark:text-zinc-400">
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-zinc-300 text-violet-600 focus:ring-violet-500"
                            checked={!!row.splitWithDris}
                            title="Kit a medias (50/50) con Dris"
                            onChange={(e) => {
                              const v = e.target.checked;
                              setClients((list) =>
                                list.map((c) =>
                                  c.id === row.id ? { ...c, splitWithDris: v } : c
                                )
                              );
                              patchClient(row.id, { splitWithDris: v });
                            }}
                          />
                          <span className="hidden lg:inline">A medias</span>
                        </label>
                      </td>
                      <td
                        className="border-b border-zinc-100 px-3 py-2.5 dark:border-zinc-800"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="flex flex-wrap items-center gap-1.5">
                          {paid70 ? (
                            <button
                              type="button"
                              disabled={rowBusy}
                              title="Quitar marca de cobro 70%"
                              className="rounded-md border border-zinc-300 px-2 py-1 text-xs text-zinc-700 hover:bg-zinc-100 disabled:opacity-50 dark:border-zinc-600 dark:text-zinc-300"
                              onClick={() =>
                                runAction(
                                  row.id,
                                  () => apiService.unmarkKitDigitalPaid70(row.id),
                                  'Cobro 70% desmarcado'
                                )
                              }
                            >
                              Quitar 70%
                            </button>
                          ) : (
                            <button
                              type="button"
                              disabled={rowBusy}
                              title="Marcar 70% cobrado"
                              className="rounded-md bg-emerald-600 px-2 py-1 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                              onClick={() =>
                                runAction(
                                  row.id,
                                  () => apiService.markKitDigitalPaid70(row.id),
                                  '70% marcado como cobrado'
                                )
                              }
                            >
                              70% ✓
                            </button>
                          )}
                          {paid30 ? (
                            <button
                              type="button"
                              disabled={rowBusy}
                              className="rounded-md border border-zinc-300 px-2 py-1 text-xs text-zinc-700 hover:bg-zinc-100 disabled:opacity-50 dark:border-zinc-600 dark:text-zinc-300"
                              onClick={() =>
                                runAction(
                                  row.id,
                                  () => apiService.unmarkKitDigitalPaid30(row.id),
                                  'Cobro 30% desmarcado'
                                )
                              }
                            >
                              Quitar 30%
                            </button>
                          ) : (
                            <button
                              type="button"
                              disabled={rowBusy}
                              className="rounded-md bg-emerald-600/90 px-2 py-1 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                              onClick={() =>
                                runAction(
                                  row.id,
                                  () => apiService.markKitDigitalPaid30(row.id),
                                  '30% marcado como cobrado'
                                )
                              }
                            >
                              30% ✓
                            </button>
                          )}
                          <button
                            type="button"
                            disabled={rowBusy}
                            className="rounded-md bg-indigo-600 px-2 py-1 text-xs text-white hover:bg-indigo-700 disabled:opacity-50"
                            onClick={() =>
                              runAction(row.id, () =>
                                apiService.generateKitDigitalInvoice70(row.id)
                              )
                            }
                          >
                            Fac.70
                          </button>
                          <button
                            type="button"
                            disabled={rowBusy}
                            className="rounded-md bg-indigo-500 px-2 py-1 text-xs text-white hover:bg-indigo-600 disabled:opacity-50"
                            onClick={() =>
                              runAction(row.id, () =>
                                apiService.generateKitDigitalInvoice30(row.id)
                              )
                            }
                          >
                            Fac.30
                          </button>
                          <button
                            type="button"
                            disabled={rowBusy}
                            title="Vincular factura existente (70%)"
                            className="rounded-md border border-zinc-300 p-1 dark:border-zinc-600"
                            onClick={async () => {
                              const invId = window.prompt('ID factura (70%)');
                              if (!invId) return;
                              await runAction(row.id, () =>
                                apiService.linkKitDigitalInvoice70(row.id, invId.trim())
                              );
                            }}
                          >
                            <LinkIcon className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            disabled={rowBusy}
                            className="rounded-md border border-red-200 p-1 text-red-600 hover:bg-red-50 dark:border-red-900"
                            onClick={() => handleDelete(row.id)}
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <p className="border-t border-zinc-100 px-4 py-2 text-xs text-zinc-400 dark:border-zinc-800">
          Desplaza horizontalmente si no ves todas las columnas. Fila verde = 70% y 30% cobrados.
        </p>
      </div>

      {selected && (
        <div className="rounded-xl border border-amber-200/80 bg-amber-50/60 px-4 py-3 text-sm text-amber-950 dark:border-amber-900 dark:bg-amber-950/20 dark:text-amber-100">
          <strong>{selected.clientName}</strong>
          <span className="mx-2 text-amber-700/70">·</span>
          Cobro 70%: {selected.paid70At || 'pendiente'}
          <span className="mx-2">·</span>
          Cobro 30%: {selected.paid30At || 'pendiente'}
          <span className="mx-2">·</span>
          Factura KD 70%: {selected.facturaKd70InvoiceId || '—'}
          <span className="mx-2">·</span>
          Factura KD 30%: {selected.facturaKd30InvoiceId || '—'}
          {selected.splitWithDris && (
            <>
              <span className="mx-2">·</span>
              <span className="rounded-full bg-violet-200/80 px-2 py-0.5 text-xs font-medium text-violet-900 dark:bg-violet-900/40 dark:text-violet-200">
                A medias con {selected.partnerName || 'Dris'}
              </span>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default KitDigitalPanel;
